import { hasOwnProperty } from "../../infra3d";
import { ArcgisFeatureServer } from "./api/internal";
import { createFetchError } from "../../infra3d/utils/ajax";
import { DEFAULTLAYERSTYLE, LAYERTYPE, Layer, LAYERPROPERTYTYPE } from "./internal";
import { hasZeroZValues } from "./format/utils";
/**
 * Extension of the Layer-Class for Layers using the Arcgis Feature Service REST-API.
 * @class ArcgisLayer
 */
export class ArcgisLayer extends Layer {
    /**
     * @constructor
     * @param scene
     * @param opt_options
     */
    constructor(scene, opt_options) {
        super(scene, opt_options);
        // Since ArcGIS uses coded values for enums, we need to store the mapping between code and value
        this._esriDomainValues = {};
        this.type = LAYERTYPE.ARCGISLAYER;
        const options = opt_options || {};
        this._esrigeometrytype = options.esrigeometrytype || "";
        this._objectIdField = options.objectIdField || "";
        this._esriDomainValues = options.esriDomainValues || {};
        // Initialization of the api. There are 2 possibilities:
        // 1. The api-object is passed in the option "api". (usually done, if the api has already been used bofore
        // Layer-Initialization, e.g. to load the layerdefinition).
        // 2. The baseurl, project and the apioptions are passed in the options.
        this.api = options.api || new ArcgisFeatureServer(options.baseurl, options.project, options.apioptions);
        this._layerid = options.layername || "";
        this.layername = this.api.project + "." + this.layername;
    }
    /**
     * ArcGIS-specific implementation of the feature-load-function. This function can still be overwritten
     * by a given function 'loadFeatures' in the options.
     * @param extent
     * @param srid
     */
    _loadFeatures_internal(extent, srid) {
        this.api.readfeatures(this._layerid, (pjson) => {
            const geojson = ArcgisLayer.pjsonToGeojson(pjson, this.propertydef, this._esriDomainValues);
            if (this.coordsdim == 3 && hasZeroZValues(geojson)) {
                Layer.geojsonRemoveZValues(geojson);
                this.coordsdim = 2;
            }
            this.addFeatures(geojson);
        }, (message) => {
            console.log(message);
        }, {
            where: this._applyFilter(),
            geometry: extent.minx + "," + extent.miny + "," + extent.maxx + "," + extent.maxy,
            geometryType: "esriGeometryEnvelope",
            spatialRel: "esriSpatialRelEnvelopeIntersects",
            inSR: srid,
            outSR: srid,
            outFields: "*",
            returnGeometry: true,
            returnDistinctValues: false,
            returnIdsOnly: false,
            returnCountOnly: false,
            returnZ: this.coordsdim == 3,
            returnM: false,
        });
    }
    /**
     * Saves all the changes done by the LayerEditor with the api. Resets the LayerEditor if the posts are
     * successful.
     *
     * @param inserts
     * @param updates
     * @param deletions
     * @param {(inserts: any, updates: any, deletions: any) => void} onSuccess
     * @param {(message: string) => void} onError
     * @private
     */
    _saveFeatures_internal(inserts, updates, deletions, onSuccess, onError) {
        const not_editable_fields = this.propertydef.filter((propdef) => !propdef.editable);
        const not_editable_attributes = not_editable_fields.map((propdef) => propdef.key);
        const pjson_add = ArcgisLayer.geojsonToPjson(inserts, this._objectIdField, this._esriDomainValues, not_editable_attributes, true);
        const pjson_updates = ArcgisLayer.geojsonToPjson(updates, this._objectIdField, this._esriDomainValues, not_editable_attributes, false);
        const deleted_ids = [];
        for (const i in deletions.features) {
            if (hasOwnProperty(deletions.features, i))
                deleted_ids.push(deletions.features[i].id);
        }
        this.api.applyEdits(this._layerid, pjson_add, pjson_updates, deleted_ids, (resp_pjson) => {
            const resp_inserts = JSON.parse(JSON.stringify(inserts));
            const resp_updates = JSON.parse(JSON.stringify(updates));
            const resp_deletions = JSON.parse(JSON.stringify(deletions));
            if (pjson_add.length > 0 && hasOwnProperty(resp_pjson, "addResults")) {
                if (resp_pjson.addResults.length == inserts.features.length) {
                    for (let i = 0; i < resp_inserts.features.length; i++) {
                        const add_results = resp_pjson.addResults[i];
                        let feature_id = -1;
                        if (hasOwnProperty(add_results, this._objectIdField)) {
                            feature_id = add_results[this._objectIdField];
                        }
                        else if (hasOwnProperty(add_results, "objectId")) {
                            feature_id = add_results.objectId;
                        }
                        for (const key of Object.keys(add_results)) {
                            if (resp_inserts.features[i].properties) {
                                const prop_idx = Object.keys(resp_inserts.features[i].properties)
                                    .map((x) => x.toLowerCase())
                                    .indexOf(key.toLowerCase());
                                if (prop_idx > -1) {
                                    const prop_key = Object.keys(resp_inserts.features[i].properties)[prop_idx];
                                    resp_inserts.features[i].properties[prop_key] = add_results[key];
                                }
                            }
                        }
                        resp_inserts.features[i].id = feature_id;
                    }
                }
                else {
                    if (onError) {
                        onError(createFetchError(0, "not all features could be added"));
                    }
                    return;
                }
            }
            if (pjson_updates.length > 0 && hasOwnProperty(resp_pjson, "updateResults")) {
                if (resp_pjson.updateResults.length != updates.features.length) {
                    if (onError) {
                        onError(createFetchError(0, "not all features could be updated"));
                    }
                    return;
                }
            }
            if (deleted_ids.length > 0 && hasOwnProperty(resp_pjson, "deleteResults")) {
                if (resp_pjson.deleteResults.length != deletions.features.length) {
                    if (onError) {
                        onError(createFetchError(0, "not all features could be deleted"));
                    }
                    return;
                }
            }
            if (onSuccess)
                onSuccess(resp_inserts, resp_updates, resp_deletions);
        }, (message) => {
            if (onError)
                onError(message);
        });
    }
    _exportFeatures_internal(cbs, cbf, options) {
        const countonly = typeof options.countonly === "undefined" ? false : options.countonly;
        const sorters = options.sorters || [];
        const orderByFields = [];
        for (let i = 0; i < sorters.length; i++) {
            if (sorters[i].property != "id") {
                //can't sort by id
                orderByFields.push(sorters[i].property + " " + sorters[i].direction);
            }
        }
        this.api.readfeatures(this._layerid, (pjson) => {
            if (countonly) {
                cbs(pjson.count);
            }
            else {
                const geojson = ArcgisLayer.pjsonToGeojson(pjson, this.propertydef, this._esriDomainValues);
                cbs(geojson);
            }
        }, cbf, {
            where: this._applyFilter(),
            outFields: "*",
            returnGeometry: !(typeof options.nogeom === "undefined" ? false : options.nogeom),
            returnDistinctValues: false,
            returnIdsOnly: false,
            returnCountOnly: countonly,
            returnZ: this.coordsdim == 3,
            returnM: false,
            resultOffset: options.start || 0,
            resultRecordCount: options.limit || 10000,
            outSR: options.srid || "",
            orderByFields: orderByFields.join(","),
        });
    }
    _applyFilter() {
        //apply the filter
        const whereclauses = [this._objectIdField + " > 0"];
        for (const key in this.filters) {
            if (hasOwnProperty(this.filters, key)) {
                const filter = this.filters[key];
                let sqlop = "=";
                let filterval = filter.value;
                //find propertydef for current filter
                let propertydef = null;
                for (let i = 0; i < this.propertydef.length; i++) {
                    if (this.propertydef[i].key == filter.property) {
                        propertydef = this.propertydef[i];
                        break;
                    }
                }
                if (propertydef && propertydef.datatype == LAYERPROPERTYTYPE.DATE) {
                    const isodate = new Date(filterval).toJSON();
                    const arcgisdate = isodate.replace("T", " ").replace("Z", "").slice(0, -4);
                    filterval = "DATE '" + arcgisdate + "'";
                }
                switch (filter.operator) {
                    case "eq":
                        sqlop = "=";
                        break;
                    case "like":
                        sqlop = "like";
                        filterval = "'%" + filter.value + "%'";
                        break;
                    case "gt":
                        sqlop = ">";
                        break;
                    case "lt":
                        sqlop = "<";
                        break;
                }
                whereclauses.push((filter.property == "id" ? this._objectIdField : filter.property) + " " + sqlop + " " + filterval);
            }
        }
        return whereclauses.join(" AND ");
    }
    /**
     * @param pjson
     * @returns {{type: string, features: Array, crs: {type: string, properties: {name: (number|any)}}}}
     */
    static pjsonToGeojson(pjson, propertydef, esriDomainValues) {
        const fc = {
            type: "FeatureCollection",
            features: [],
        };
        for (let i = 0; i < pjson.features.length; i++) {
            let geometry = {};
            const pgeometry = pjson.features[i].geometry;
            if (pgeometry) {
                switch (pjson.geometryType) {
                    case "esriGeometryPoint": {
                        const coordinates = [pgeometry.x, pgeometry.y];
                        if (pjson.hasZ && hasOwnProperty(pgeometry, "z"))
                            coordinates.push(pgeometry.z);
                        geometry = {
                            type: "Point",
                            coordinates: coordinates,
                        };
                        break;
                    }
                    case "esriGeometryMultipoint":
                        geometry = {
                            type: "MultiPoint",
                            coordinates: pgeometry.points,
                        };
                        break;
                    case "esriGeometryPolyline":
                        if (pgeometry.paths.length > 0) {
                            if (pgeometry.paths.length > 1) {
                                geometry = {
                                    type: "MultiLineString",
                                    coordinates: pgeometry.paths,
                                };
                            }
                            else {
                                geometry = {
                                    type: "LineString",
                                    coordinates: pgeometry.paths[0],
                                };
                            }
                        }
                        break;
                    case "esriGeometryPolygon":
                        if (pgeometry.rings.length > 0) {
                            if (pgeometry.rings.length > 1) {
                                const polygons = [];
                                for (const ring of pgeometry.rings) {
                                    polygons.push([ring]);
                                }
                                geometry = {
                                    type: "MultiPolygon",
                                    coordinates: polygons,
                                };
                            }
                            else {
                                geometry = {
                                    type: "Polygon",
                                    coordinates: [pgeometry.rings[0]],
                                };
                            }
                        }
                        break;
                    case "esriGeometryEnvelope":
                        if (pjson.hasZ && hasOwnProperty(pgeometry, "zmin")) {
                            geometry = {
                                type: "Polygon",
                                coordinates: [
                                    [
                                        [pgeometry.xmin, pgeometry.ymin, pgeometry.zmin],
                                        [pgeometry.xmax, pgeometry.ymin, pgeometry.zmin],
                                        [pgeometry.xmax, pgeometry.ymax, pgeometry.zmin],
                                        [pgeometry.xmin, pgeometry.ymax, pgeometry.zmin],
                                        [pgeometry.xmin, pgeometry.ymin, pgeometry.zmin],
                                    ],
                                ],
                            };
                        }
                        else {
                            geometry = {
                                type: "Polygon",
                                coordinates: [
                                    [
                                        [pgeometry.xmin, pgeometry.ymin],
                                        [pgeometry.xmax, pgeometry.ymin],
                                        [pgeometry.xmax, pgeometry.ymax],
                                        [pgeometry.xmin, pgeometry.ymax],
                                        [pgeometry.xmin, pgeometry.ymin],
                                    ],
                                ],
                            };
                        }
                        break;
                }
            }
            const properties = pjson.features[i].attributes;
            for (const propdef of propertydef) {
                if (propdef.datatype == LAYERPROPERTYTYPE.ENUM) {
                    if (properties[propdef.key] != null) {
                        const esriCodedValues = esriDomainValues[propdef.key];
                        const codeEntry = esriCodedValues.find((cv) => cv.code === properties[propdef.key]);
                        if (codeEntry) {
                            properties[propdef.key] = codeEntry.name;
                        }
                    }
                }
            }
            const objectIdFieldName = ArcgisLayer.objectIDFieldNameFromPjson(pjson);
            const f = {
                type: "Feature",
                geometry: geometry,
                properties: properties,
                id: pjson.features[i].attributes[objectIdFieldName],
            };
            delete properties[objectIdFieldName];
            fc.features.push(f);
            if (pjson.spatialReference) {
                fc["crs"] = {
                    type: "name",
                    properties: {
                        name: "EPSG:" + pjson.spatialReference.wkid,
                    },
                };
            }
        }
        return fc;
    }
    static geojsonToPjson(geojson, objectIdFieldName, domains_values, excludeFields = [], excludeIdField = true) {
        const epsg = parseInt(geojson.crs.properties.name.split(":")[1]);
        const features = [];
        for (let i = 0; i < geojson.features.length; i++) {
            const f = geojson.features[i];
            let geometry = {};
            switch (f.geometry.type) {
                case "Point":
                    geometry = {
                        x: f.geometry.coordinates[0],
                        y: f.geometry.coordinates[1],
                        spatialReference: { wkid: epsg },
                    };
                    if (f.geometry.coordinates.length == 3)
                        geometry = {
                            x: f.geometry.coordinates[0],
                            y: f.geometry.coordinates[1],
                            z: f.geometry.coordinates[2],
                            spatialReference: { wkid: epsg },
                        };
                    break;
                case "MultiPoint":
                    geometry = {
                        hasZ: f.geometry.coordinates[0].length == 3,
                        points: f.geometry.coordinates,
                        spatialReference: { wkid: epsg },
                    };
                    break;
                case "LineString":
                    geometry = {
                        hasZ: f.geometry.coordinates[0].length == 3,
                        paths: [f.geometry.coordinates],
                        spatialReference: { wkid: epsg },
                    };
                    break;
                case "Polygon": {
                    //Change order of the coords-array. Exterior rings must be oriented clockwise.
                    const _ring = this._setCoordarrayCounterClockwise(f.geometry.coordinates[0]);
                    geometry = {
                        hasZ: f.geometry.coordinates[0][0].length == 3,
                        rings: [_ring],
                        spatialReference: { wkid: epsg },
                    };
                    break;
                }
            }
            const attributes = JSON.parse(JSON.stringify(f.properties));
            if (!excludeIdField)
                attributes[objectIdFieldName] = f.id;
            const final_attributes = {};
            for (const attribute_key of Object.keys(attributes)) {
                if (excludeFields.indexOf(attribute_key) == -1) {
                    if (domains_values[attribute_key]) {
                        const esriCodedValues = domains_values[attribute_key];
                        const codeEntry = esriCodedValues.find((cv) => cv.name === attributes[attribute_key]);
                        if (codeEntry) {
                            final_attributes[attribute_key] = codeEntry.code;
                            continue;
                        }
                    }
                    final_attributes[attribute_key] = attributes[attribute_key];
                }
            }
            const feature = {
                geometry: geometry,
                attributes: final_attributes,
            };
            features.push(feature);
        }
        return features;
    }
    static _setCoordarrayCounterClockwise(coords) {
        //Gauss'sche Flaechenformel
        let indic = 0.0;
        for (let i = 0; i < coords.length; i++) {
            let idx_m1 = (i - 1) % coords.length;
            if (i - 1 < 0)
                idx_m1 = (i - 1 + coords.length) % coords.length;
            const idx_p1 = (i + 1) % coords.length;
            indic += coords[i][1] * (coords[idx_m1][0] - coords[idx_p1][0]);
        }
        if (indic >= 0.0)
            return coords.reverse();
        return coords;
    }
    static optionsFromLayerDescription(pjson, opt_options) {
        const override_options = opt_options || {};
        let options = {};
        options["layername"] = pjson.id;
        options["title"] = pjson.name;
        //Geometry-Type
        switch (pjson.geometryType) {
            case "esriGeometryPoint":
                options["geometrytype"] = "Point";
                break;
            case "esriGeometryMultipoint":
                options["geometrytype"] = "MultiPoint";
                break;
            case "esriGeometryPolyline":
                options["geometrytype"] = "LineString";
                break;
            case "esriGeometryPolygon":
                options["geometrytype"] = "Polygon";
                break;
            case "esriGeometryEnvelope":
                options["geometrytype"] = "Polygon";
                break;
        }
        options["esrigeometrytype"] = pjson.geometryType;
        //Primary-Key
        const objectIdField = ArcgisLayer.objectIDFieldNameFromPjson(pjson);
        options["objectIdField"] = objectIdField;
        //Properties
        options["propertydef"] = [];
        options["esriDomainValues"] = {};
        for (let i = 0; i < pjson.fields.length; i++) {
            const pfield = pjson.fields[i];
            if (pfield.name !== objectIdField) {
                const prop = {};
                prop["key"] = pfield.name;
                prop["name"] = pfield.alias;
                prop["datatype"] = LAYERPROPERTYTYPE.STRING;
                prop["defaultvalue"] = pfield.defaultValue || "";
                prop["editable"] = typeof pfield.editable === "undefined" ? true : pfield.editable;
                switch (pfield.type) {
                    case "esriFieldTypeInteger":
                        prop["datatype"] = LAYERPROPERTYTYPE.INTEGER;
                        prop["defaultvalue"] = pfield.defaultValue || 0;
                        break;
                    case "esriFieldTypeString":
                        prop["datatype"] = LAYERPROPERTYTYPE.STRING;
                        prop["defaultvalue"] = pfield.defaultValue || "";
                        break;
                    case "esriFieldTypeDouble":
                        prop["datatype"] = LAYERPROPERTYTYPE.NUMBER;
                        prop["defaultvalue"] = pfield.defaultValue || 0;
                        break;
                    case "esriFieldTypeSmallInteger":
                        prop["datatype"] = LAYERPROPERTYTYPE.INTEGER;
                        prop["defaultvalue"] = pfield.defaultValue || 0;
                        break;
                    case "esriFieldTypeDate":
                        prop["datatype"] = LAYERPROPERTYTYPE.DATE;
                        prop["defaultvalue"] = pfield.defaultValue || 0;
                        prop["dateformat"] = "time";
                        break;
                }
                if (hasOwnProperty(pfield, "domain")) {
                    if (pfield.domain != null) {
                        if (pfield.domain.type == "codedValue") {
                            const values = pfield.domain.codedValues.map((codedvalue) => {
                                return codedvalue.name;
                            });
                            prop["datatype"] = LAYERPROPERTYTYPE.ENUM;
                            prop["values"] = values;
                            //store mapping code -> value for later use during import/export
                            options["esriDomainValues"][pfield.name] = pfield.domain.codedValues;
                        }
                    }
                }
                options["propertydef"].push(prop);
            }
        }
        //Coordsdim
        options["coordsdim"] = pjson.hasZ ? 3 : 2;
        //global transparency
        let global_transparency = 100; // 0 = opaque, 100=transparent
        if (hasOwnProperty(pjson, "drawingInfo")) {
            if (hasOwnProperty(pjson.drawingInfo, "transparency")) {
                global_transparency = pjson.drawingInfo.transparency;
            }
        }
        //Styles
        const colorfromesri = (esricolor) => {
            let alpha = esricolor[3] / 255.0; // 1 = opaque, 0 = transparent
            alpha *= (100 - global_transparency) / 100;
            return [esricolor[0] / 255.0, esricolor[1] / 255.0, esricolor[2] / 255.0, alpha];
        };
        const getesrilineparams = (symbol) => {
            const dashmap = {
                esriSLSDash: [5, 4],
                esriSLSDashDot: [4, 2],
                esriSLSDashDotDot: [4, 2],
                esriSLSDot: [2, 5],
                esriSLSNull: [0, 2],
                esriSLSSolid: [5, 0],
            };
            let dashes = {};
            if (["esriSLSDash", "esriSLSDashDot", "esriSLSDashDotDot", "esriSLSDot"].indexOf(symbol.style) > -1) {
                dashes = {
                    "stroke-gap-width": dashmap[symbol.style][1] * symbol.width,
                    "stroke-dash-width": dashmap[symbol.style][0] * symbol.width,
                };
            }
            return {
                stroke: colorfromesri(symbol.color),
                "stroke-width": symbol.width,
                ...dashes,
            };
        };
        const parsesymbol = (symbol, style, highlightstyle, filter = "", rule_name = "") => {
            switch (symbol.type) {
                case "esriSMS": {
                    const symbolmap = {
                        esriSMSCircle: "circle",
                        esriSMSCross: "cross",
                        "esriSMSDiamond ": "square",
                        esriSMSSquare: "square",
                        esriSMSX: "crosshairs", // not idea what SMSX looks like?
                        esriSMSTriangle: "square",
                    };
                    const fontmarker = symbolmap[symbol.style] || "circle-o";
                    const _layerstyle_sms = {
                        name: rule_name,
                        filter: filter,
                        symbolizer: "point",
                        params: {
                            "font-marker": {
                                name: fontmarker,
                                rotation: symbol.angle,
                                fontsize: symbol.size * 2,
                            },
                            size: symbol.size * 2,
                            stroke: colorfromesri(symbol.color),
                        },
                    };
                    style["rules"].push(_layerstyle_sms);
                    const thighlightstyle_sms = JSON.parse(JSON.stringify(_layerstyle_sms));
                    thighlightstyle_sms["params"]["stroke"] = [1, 1, 0, 1];
                    highlightstyle["rules"].push(thighlightstyle_sms);
                    break;
                }
                case "esriSLS": {
                    const layerstyle_sls = {
                        name: rule_name,
                        filter: filter,
                        symbolizer: "line",
                        params: getesrilineparams(symbol),
                    };
                    style["rules"].push(layerstyle_sls);
                    const thighlightstyle_sls = JSON.parse(JSON.stringify(layerstyle_sls));
                    thighlightstyle_sls["params"]["stroke"] = [1, 1, 0, 1];
                    highlightstyle["rules"].push(thighlightstyle_sls);
                    highlightstyle["rules"].push({
                        name: rule_name,
                        filter: filter,
                        symbolizer: "point",
                        params: {
                            size: layerstyle_sls["params"]["stroke-width"] * 2.0,
                            stroke: [1, 1, 0, 1],
                        },
                    });
                    break;
                }
                case "esriSFS": {
                    const layerstyle_sfs_point = {
                        name: rule_name,
                        filter: filter,
                        symbolizer: "point",
                        params: {
                            size: 0.1,
                            stroke: [1, 1, 1, 0],
                        },
                    };
                    const layerstyle_sfs_line = {
                        name: rule_name,
                        filter: filter,
                        symbolizer: "line",
                        params: getesrilineparams(symbol.outline),
                    };
                    const layerstyle_sfs_polygon = {
                        name: rule_name,
                        filter: filter,
                        symbolizer: "polygon",
                        params: {
                            fill: colorfromesri(symbol.color),
                        },
                    };
                    style["rules"].push(layerstyle_sfs_point);
                    style["rules"].push(layerstyle_sfs_line);
                    style["rules"].push(layerstyle_sfs_polygon);
                    const thighlightstyle_sfs_line = JSON.parse(JSON.stringify(layerstyle_sfs_line));
                    const thighlightstyle_sfs_polygon = JSON.parse(JSON.stringify(layerstyle_sfs_polygon));
                    thighlightstyle_sfs_line["params"]["stroke"] = [1, 1, 0, 1];
                    thighlightstyle_sfs_polygon["params"]["fill"] = [1, 1, 0, 0.5];
                    highlightstyle["rules"].push(thighlightstyle_sfs_line);
                    highlightstyle["rules"].push(thighlightstyle_sfs_polygon);
                    break;
                }
                case "esriPMS": {
                    const layerstyle = {
                        name: rule_name,
                        filter: filter,
                        symbolizer: "point",
                        params: {
                            billboard: "data:" + symbol.contentType + ";base64," + symbol.imageData,
                            size: Math.max(symbol.width, symbol.height) / 2.0,
                            stroke: [1, 1, 1, 1],
                        },
                    };
                    style["rules"].push(layerstyle);
                    const thighlightstyle = JSON.parse(JSON.stringify(layerstyle));
                    thighlightstyle["params"]["stroke"] = [1, 1, 0, 1];
                    highlightstyle["rules"].push(thighlightstyle);
                    break;
                }
            }
        };
        const parserenderer = (renderer, options) => {
            options["style"] = {
                stylename: "layerstyle",
                rules: [],
            };
            options["highlightstyle"] = {
                stylename: "highlightstyle",
                rules: [],
            };
            switch (renderer.type) {
                case "simple":
                    parsesymbol(renderer.symbol, options["style"], options["highlightstyle"]);
                    break;
                case "uniqueValue":
                    if (renderer.defaultSymbol) {
                        parsesymbol(renderer.defaultSymbol, options["style"], options["highlightstyle"], "", renderer.defaultLabel);
                    }
                    else {
                        options["style"] = JSON.parse(DEFAULTLAYERSTYLE.EMPTY.toJSON());
                    }
                    for (const i in renderer.uniqueValueInfos) {
                        const uniquevalueinfo = renderer.uniqueValueInfos[i];
                        const domain_values = options["esriDomainValues"][renderer.field1];
                        if (domain_values) {
                            const nameEntry = domain_values.find((dv) => String(dv.code) === uniquevalueinfo.value);
                            if (nameEntry) {
                                uniquevalueinfo.value = nameEntry.name;
                            }
                        }
                        const filter = renderer.field1 + "==" + uniquevalueinfo.value;
                        parsesymbol(uniquevalueinfo.symbol, options["style"], options["highlightstyle"], filter, uniquevalueinfo.label);
                    }
                    break;
            }
            if (options["style"]["rules"].length == 0) {
                options["style"] = JSON.parse(DEFAULTLAYERSTYLE.STYLE.toJSON());
            }
        };
        if (hasOwnProperty(pjson, "drawingInfo")) {
            if (hasOwnProperty(pjson.drawingInfo, "renderer")) {
                const renderer = pjson.drawingInfo.renderer;
                parserenderer(renderer, options);
            }
        }
        options = { ...options, ...override_options };
        return options;
    }
    static objectIDFieldNameFromPjson(pjson) {
        if (pjson.objectIdFieldName) {
            return pjson.objectIdFieldName;
        }
        if (pjson.fields) {
            for (const field of pjson.fields) {
                if (field.type == "esriFieldTypeOID") {
                    return field.name;
                }
            }
        }
        return "OBJECTID";
    }
}
//# sourceMappingURL=arcgislayer.js.map